import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../utils/auth";
import { isAdmin } from "../../utils/roles";
import {
  searchOrdersByPhone,
  fetchOrderById,
  updateOrderStatus,
  markOrderSentToFincart,
  sendSentToCartWhatsApp,
} from "../../api/OrderAPIs";
import {
  fetchFincartGovernorates,
  getDefaultPickupLocationId,
  sendOrderToFincart,
  saveFincartOrderId,
  getStoredFincartOrderId,
} from "../../api/FincartAPIs";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ArrowRight,
  RefreshCw,
  Phone,
  Hash,
  Printer,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  Building2,
  AlertTriangle,
  PauseCircle,
  Archive,
  XCircle,
  Clock,
  Package,
  Ban,
  DollarSign,
  Layers,
  ShoppingCart,
  Send,
  Truck,
  Shirt,
} from "lucide-react";

const PRINTING_STATUSES = [
  { value: "Printing Hold", label: "Printing Hold", icon: PauseCircle, color: "bg-slate-700 hover:bg-slate-600" },
  { value: "Printed", label: "Printed", icon: CheckCircle, color: "bg-teal-600 hover:bg-teal-500" },
  { value: "Girabatcom", label: "Girabatcom", icon: Building2, color: "bg-indigo-600 hover:bg-indigo-500" },
  { value: "Printlet", label: "Printlet", icon: Package, color: "bg-fuchsia-600 hover:bg-fuchsia-500" },
  { value: "TeePlus", label: "Tee Plus", icon: Shirt, color: "bg-emerald-600 hover:bg-emerald-500" },
  { value: "Incomplete", label: "Incomplete", icon: AlertTriangle, color: "bg-amber-600 hover:bg-amber-500" },
];

const OTHER_STATUSES = [
  { value: "New", label: "New", icon: RefreshCw, color: "bg-blue-600 hover:bg-blue-500" },
  { value: "Confirmed", label: "Confirmed", icon: CheckCircle, color: "bg-green-600 hover:bg-green-500" },
  { value: "Hold", label: "Hold", icon: PauseCircle, color: "bg-yellow-600 hover:bg-yellow-500" },
  { value: "Changes", label: "Changes", icon: Layers, color: "bg-orange-600 hover:bg-orange-500" },
  { value: "Old", label: "Old", icon: Archive, color: "bg-gray-600 hover:bg-gray-500" },
  { value: "Spam", label: "Spam", icon: Ban, color: "bg-rose-700 hover:bg-rose-600" },
  { value: "Canceled", label: "Canceled", icon: XCircle, color: "bg-red-600 hover:bg-red-500" },
  { value: "Cancelled by Customer", label: "Cancelled by Customer", icon: XCircle, color: "bg-red-800 hover:bg-red-700" },
  { value: "Deposit Required", label: "Deposit Required", icon: DollarSign, color: "bg-violet-600 hover:bg-violet-500" },
  { value: "Custom", label: "Custom", icon: Package, color: "bg-fuchsia-600 hover:bg-fuchsia-500" },
  { value: "Under Purchase", label: "Under Purchase", icon: ShoppingCart, color: "bg-cyan-700 hover:bg-cyan-600" },
  { value: "Waiting Confirming", label: "Waiting Confirming", icon: Clock, color: "bg-yellow-700 hover:bg-yellow-600" },
  { value: "Confirming", label: "Confirming", icon: Send, color: "bg-lime-700 hover:bg-lime-600" },
];

/** Normalize Egyptian phones incl. stray leading 2 / country code 20 */
const normalizePhoneNumber = (phone) => {
  if (!phone) return phone;
  let cleaned = String(phone).replace(/[^\d+]/g, "");
  if (cleaned.startsWith("+")) cleaned = cleaned.slice(1);
  if (cleaned.startsWith("20") && cleaned.length === 12) cleaned = "0" + cleaned.slice(2);
  if (cleaned.startsWith("2") && cleaned.length === 11) cleaned = "0" + cleaned.slice(1);
  if (cleaned.length === 10 && /^1[0125]/.test(cleaned)) cleaned = "0" + cleaned;
  if (/^01[0125]\d{8}$/.test(cleaned)) return cleaned;
  return String(phone).replace(/\D/g, "");
};

const toInternational = (localOrAny) => {
  const digits = String(localOrAny || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.startsWith("20") && digits.length === 12) return digits;
  if (digits.startsWith("0") && digits.length === 11) return "2" + digits;
  if (digits.length === 10 && /^1[0125]/.test(digits)) return "20" + digits;
  return digits;
};

const parseLines = (text) =>
  text
    .split(/[\r\n]+/)
    .map((l) => l.trim())
    .filter(Boolean);

const pickLatestOrder = (orders) => {
  if (!Array.isArray(orders) || orders.length === 0) return null;
  return [...orders].sort((a, b) => {
    const idA = Number(a.orderId ?? a.orderNumber ?? 0);
    const idB = Number(b.orderId ?? b.orderNumber ?? 0);
    if (idB !== idA) return idB - idA;
    const dateA = new Date(a.date || 0).getTime();
    const dateB = new Date(b.date || 0).getTime();
    return dateB - dateA;
  })[0];
};

/** Map raw API order → shape expected by sendOrderToFincart */
const toFincartOrder = (order) => {
  const orderId = order.orderId ?? order.orderNumber;
  const items = [];
  for (const detail of order.orderDetails || []) {
    const typeName =
      detail.type?.name ||
      detail.product?.name ||
      detail.productVariant?.product?.name ||
      "Item";
    const qty = Math.max(Number(detail.quantity) || 1, 1);
    for (let i = 0; i < qty; i++) {
      items.push({
        type: typeName,
        name: detail.design?.name || detail.design?.Name || typeName,
      });
    }
  }
  const storedId = getStoredFincartOrderId(orderId);
  return {
    orderNumber: orderId,
    items,
    total: order.totalAmount,
    address: order.address?.fullAddress || "",
    state: order.address?.state || "",
    city: order.address?.city || "",
    apartment: order.address?.apartment || "",
    phoneNumber: order.phoneNumber || "",
    phoneNumber2: order.phoneNumber2 || "",
    name: order.name || "",
    status: order.status || "",
    sentToFincart: !!order.sentToFincart || !!order.shippingOrderId || !!storedId,
    fincartOrderId: order.shippingOrderId || storedId,
  };
};

const BulkStatusUpdatePage = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const [inputMode, setInputMode] = useState("orders"); // "orders" | "phones"
  const [rawInput, setRawInput] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("Printing Hold");
  const [reason, setReason] = useState("");
  const [otherOpen, setOtherOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fincartLoading, setFincartLoading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [results, setResults] = useState(null);
  const [fincartResults, setFincartResults] = useState(null);
  const busy = loading || fincartLoading;

  const lineCount = parseLines(rawInput).length;

  const resolveByPhone = async (rawPhone) => {
    const local = normalizePhoneNumber(rawPhone);
    const international = toInternational(local || rawPhone);
    const candidates = [...new Set([local, international, String(rawPhone).replace(/\D/g, "")].filter(Boolean))];

    let lastError = null;
    for (const candidate of candidates) {
      try {
        const orders = await searchOrdersByPhone(candidate);
        const latest = pickLatestOrder(orders);
        if (latest) {
          const orderId = latest.orderId ?? latest.orderNumber;
          // Full fetch so Fincart has address + items
          const full = await fetchOrderById(orderId);
          return {
            orderId,
            currentStatus: full?.status ?? latest.status,
            phone: local || international || rawPhone,
            order: full,
          };
        }
      } catch (err) {
        lastError = err;
      }
    }
    throw lastError || new Error("لا يوجد طلب لهذا الرقم");
  };

  const resolveByOrderId = async (rawId) => {
    const id = String(rawId).replace(/\D/g, "");
    if (!id) throw new Error("رقم طلب غير صالح");
    const order = await fetchOrderById(id);
    if (!order) throw new Error("الطلب غير موجود");
    return {
      orderId: order.orderId ?? order.orderNumber ?? id,
      currentStatus: order.status,
      phone: order.phoneNumber,
      order,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const lines = parseLines(rawInput);
    if (lines.length === 0) {
      toast.error(inputMode === "phones" ? "أدخل رقم موبايل واحد على الأقل" : "أدخل رقم طلب واحد على الأقل");
      return;
    }
    if (!selectedStatus) {
      toast.error("اختر حالة");
      return;
    }
    if (!window.confirm(`تحديث ${lines.length} عنصر إلى "${selectedStatus}"؟`)) return;

    setLoading(true);
    setResults(null);
    setProgress({ done: 0, total: lines.length });

    const rowResults = [];
    let successCount = 0;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      try {
        const resolved =
          inputMode === "phones" ? await resolveByPhone(line) : await resolveByOrderId(line);

        await updateOrderStatus(
          resolved.orderId,
          selectedStatus,
          reason.trim() || null
        );

        successCount += 1;
        rowResults.push({
          input: line,
          success: true,
          orderId: resolved.orderId,
          fromStatus: resolved.currentStatus,
          toStatus: selectedStatus,
          phone: resolved.phone,
        });
      } catch (err) {
        rowResults.push({
          input: line,
          success: false,
          error: err.message || "فشل التحديث",
        });
      }
      setProgress({ done: i + 1, total: lines.length });
    }

    setResults({ processed: successCount, total: lines.length, results: rowResults });
    if (successCount > 0) {
      toast.success(`تم تحديث ${successCount} من ${lines.length}`);
    } else {
      toast.error("لم يتم تحديث أي طلب");
    }
    setLoading(false);
  };

  const handleSendToFincart = async () => {
    const lines = parseLines(rawInput);
    if (lines.length === 0) {
      toast.error(inputMode === "phones" ? "أدخل رقم موبايل واحد على الأقل" : "أدخل رقم طلب واحد على الأقل");
      return;
    }
    if (
      !window.confirm(
        `إرسال ${lines.length} طلب إلى Fincart للشحن؟\n\nلن يتم تغيير حالة الطلب — حدّث يدوياً بعدين.`
      )
    ) {
      return;
    }

    setFincartLoading(true);
    setFincartResults(null);
    setProgress({ done: 0, total: lines.length });

    let pickupLocationId;
    let governorates;
    try {
      [pickupLocationId, governorates] = await Promise.all([
        getDefaultPickupLocationId(),
        fetchFincartGovernorates(),
      ]);
    } catch (err) {
      toast.error(err.message || "فشل التحضير للإرسال إلى Fincart");
      setFincartLoading(false);
      return;
    }

    const rowResults = [];
    let successCount = 0;
    let alreadyCount = 0;
    let skippedCount = 0;
    const phonesForWhatsApp = [];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      try {
        const resolved =
          inputMode === "phones" ? await resolveByPhone(line) : await resolveByOrderId(line);
        const fincartOrder = toFincartOrder(resolved.order);

        if (fincartOrder.sentToFincart || fincartOrder.fincartOrderId) {
          skippedCount += 1;
          rowResults.push({
            input: line,
            success: true,
            skipped: true,
            orderId: fincartOrder.orderNumber,
            fincartOrderId: fincartOrder.fincartOrderId,
            message: "مُرسل لـ Fincart بالفعل",
          });
          setProgress({ done: i + 1, total: lines.length });
          continue;
        }

        if (!String(fincartOrder.city || "").trim()) {
          throw new Error("بدون مدينة/منطقة من قائمة Fincart — عدّل العنوان أولاً");
        }

        const result = await sendOrderToFincart(
          fincartOrder,
          pickupLocationId,
          governorates
        );
        const fincartOrderId = result?.fincartOrderId || result?.data?.orderId || null;
        const alreadyInFincart = !!result?.alreadyInFincart;
        const trackingNumber = result?.trackingNumber || result?.orderCode || null;

        if (fincartOrderId) {
          saveFincartOrderId(fincartOrder.orderNumber, fincartOrderId);
        }

        try {
          await markOrderSentToFincart(fincartOrder.orderNumber, true, {
            shippingOrderId: fincartOrderId,
            trackingNumber,
          });
        } catch (persistErr) {
          console.error("فشل حفظ SentToFincart:", persistErr);
        }

        if (fincartOrder.phoneNumber) {
          phonesForWhatsApp.push(fincartOrder.phoneNumber);
        }

        if (alreadyInFincart) {
          alreadyCount += 1;
        } else {
          successCount += 1;
        }

        rowResults.push({
          input: line,
          success: true,
          orderId: fincartOrder.orderNumber,
          fincartOrderId,
          trackingNumber,
          alreadyInFincart,
          message: alreadyInFincart ? "موجود في Fincart (مكرر)" : "تم الإرسال",
        });
      } catch (err) {
        rowResults.push({
          input: line,
          success: false,
          error: err.message || "فشل الإرسال لـ Fincart",
        });
      }
      setProgress({ done: i + 1, total: lines.length });
    }

    if (phonesForWhatsApp.length > 0) {
      try {
        await sendSentToCartWhatsApp(phonesForWhatsApp);
      } catch (waErr) {
        console.error("فشل إرسال تمبلت sent_to_cart:", waErr);
        toast.warning(
          "تم الإرسال لـ Fincart لكن فشل إرسال رسالة واتساب (sent_to_cart) للعميل"
        );
      }
    }

    setFincartResults({
      processed: successCount,
      already: alreadyCount,
      skipped: skippedCount,
      total: lines.length,
      results: rowResults,
    });

    const failed = lines.length - successCount - alreadyCount - skippedCount;
    if (successCount > 0 || alreadyCount > 0) {
      toast.success(
        `Fincart: ${successCount} جديد` +
          (alreadyCount ? `، ${alreadyCount} مكرر` : "") +
          (skippedCount ? `، ${skippedCount} متخطى` : "") +
          (failed ? `، ${failed} فشل` : "")
      );
    } else if (skippedCount > 0 && failed === 0) {
      toast.info(`كل الطلبات مُرسلة لـ Fincart بالفعل (${skippedCount})`);
    } else {
      toast.error("لم يتم إرسال أي طلب لـ Fincart");
    }
    setFincartLoading(false);
  };

  if (!isAdmin(authState.role)) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white bg-gray-900">
        غير مصرح — للـ Admin فقط
      </div>
    );
  }

  const StatusButton = ({ item, selected }) => {
    const Icon = item.icon;
    return (
      <button
        type="button"
        onClick={() => setSelectedStatus(item.value)}
        disabled={busy}
        className={`flex items-center justify-center gap-2 rounded-xl py-3 px-3 text-sm font-semibold transition border-2 ${
          selected
            ? `${item.color} text-white border-white/80 ring-2 ring-white/40 scale-[1.02]`
            : `${item.color} text-white border-transparent opacity-80 hover:opacity-100`
        } disabled:opacity-40`}
      >
        <Icon size={16} />
        {item.label}
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-indigo-950 text-white p-6" dir="rtl">
      <ToastContainer position="top-center" theme="dark" />
      <div className="max-w-3xl mx-auto">
        <button
          onClick={() => navigate("/dashboard")}
          className="mb-6 flex items-center gap-2 text-indigo-200 hover:text-white"
        >
          <ArrowRight size={18} />
          العودة للوحة التحكم
        </button>

        <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-6 shadow-xl">
          <div className="flex items-center gap-3 mb-2">
            <RefreshCw className="text-indigo-300" />
            <h1 className="text-2xl font-bold">تحديث حالة جماعي + Fincart</h1>
          </div>
          <p className="text-indigo-100/90 mb-5 text-sm leading-relaxed">
            الصق أرقام الطلبات أو أرقام الموبايل (سطر لكل رقم). تقدر تحدث الحالة و/أو تبعت نفس الأرقام لـ Fincart.
            عند البحث بالموبايل يتم استخدام{" "}
            <strong>آخر طلب فقط</strong> للعميل.
          </p>

          {/* Input mode */}
          <div className="flex gap-2 mb-4">
            <button
              type="button"
              onClick={() => setInputMode("orders")}
              disabled={busy}
              className={`flex-1 flex items-center justify-center gap-2 rounded-xl py-2.5 font-medium transition border ${
                inputMode === "orders"
                  ? "bg-indigo-600 border-indigo-400 text-white"
                  : "bg-black/20 border-white/15 text-indigo-100 hover:bg-black/30"
              }`}
            >
              <Hash size={16} />
              أرقام طلبات
            </button>
            <button
              type="button"
              onClick={() => setInputMode("phones")}
              disabled={busy}
              className={`flex-1 flex items-center justify-center gap-2 rounded-xl py-2.5 font-medium transition border ${
                inputMode === "phones"
                  ? "bg-indigo-600 border-indigo-400 text-white"
                  : "bg-black/20 border-white/15 text-indigo-100 hover:bg-black/30"
              }`}
            >
              <Phone size={16} />
              أرقام موبايل
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <textarea
              value={rawInput}
              onChange={(e) => setRawInput(e.target.value)}
              rows={10}
              disabled={busy}
              placeholder={
                inputMode === "orders"
                  ? "32111\n32112\n32114"
                  : "01002154996\n201555069856\n21002154996"
              }
              className="w-full rounded-xl bg-black/30 border border-white/20 p-4 text-white placeholder-indigo-200/40 focus:outline-none focus:ring-2 focus:ring-indigo-400 font-mono text-left leading-relaxed"
              dir="ltr"
            />
            <p className="mt-2 text-xs text-indigo-200/70">
              {lineCount > 0 ? `${lineCount} سطر` : "لا توجد أسطر بعد"}
            </p>

            {/* Printing statuses */}
            <div className="mt-6">
              <p className="text-sm font-bold text-indigo-100 mb-3 flex items-center gap-2">
                <Printer size={16} />
                حالات الطباعة
              </p>
              <div className="grid grid-cols-2 gap-2">
                {PRINTING_STATUSES.map((item) => (
                  <StatusButton
                    key={item.value}
                    item={item}
                    selected={selectedStatus === item.value}
                  />
                ))}
              </div>
            </div>

            {/* Other statuses — collapse */}
            <div className="mt-4 border border-white/15 rounded-xl overflow-hidden">
              <button
                type="button"
                onClick={() => setOtherOpen((v) => !v)}
                className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-black/25 hover:bg-black/35 transition text-sm font-semibold"
              >
                <span className="flex items-center gap-2">
                  <Layers size={16} className="text-indigo-300" />
                  باقي الحالات
                  {OTHER_STATUSES.some((s) => s.value === selectedStatus) && (
                    <span className="text-xs bg-indigo-500/40 px-2 py-0.5 rounded-full">
                      مختار: {selectedStatus}
                    </span>
                  )}
                </span>
                {otherOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </button>
              <div
                className={`transition-all duration-300 overflow-hidden ${
                  otherOpen ? "max-h-[800px] opacity-100" : "max-h-0 opacity-0"
                }`}
              >
                <div className="p-3 grid grid-cols-2 gap-2">
                  {OTHER_STATUSES.map((item) => (
                    <StatusButton
                      key={item.value}
                      item={item}
                      selected={selectedStatus === item.value}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-xs text-indigo-200 mb-1">ملاحظة / سبب (اختياري)</label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={busy}
                placeholder="سبب التحديث الجماعي..."
                className="w-full rounded-xl bg-black/30 border border-white/20 p-3 text-white placeholder-indigo-200/40 focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
            </div>

            <button
              type="submit"
              disabled={busy || lineCount === 0}
              className="mt-5 w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 rounded-xl py-3.5 font-semibold transition"
            >
              <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
              {loading
                ? `جاري التحديث... (${progress.done}/${progress.total})`
                : `تحديث إلى ${selectedStatus}`}
            </button>
          </form>

          {/* Fincart — same order IDs */}
          <div className="mt-6 pt-5 border-t border-white/15">
            <p className="text-sm font-bold text-orange-100 mb-2 flex items-center gap-2">
              <Truck size={16} className="text-orange-300" />
              إرسال إلى Fincart
            </p>
            <p className="text-xs text-indigo-200/80 mb-3 leading-relaxed">
              يستخدم نفس الأرقام فوق. الطلبات المُرسلة مسبقاً هتتخطى. الحالة مش هتتغيّر.
            </p>
            <button
              type="button"
              onClick={handleSendToFincart}
              disabled={busy || lineCount === 0}
              className="w-full flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-500 disabled:opacity-50 rounded-xl py-3.5 font-semibold transition"
            >
              <Truck size={18} className={fincartLoading ? "animate-pulse" : ""} />
              {fincartLoading
                ? `جاري الإرسال لـ Fincart... (${progress.done}/${progress.total})`
                : "إرسال الأرقام إلى Fincart"}
            </button>
          </div>

          {results && (
            <div className="mt-6 bg-black/20 rounded-xl p-4 border border-white/10">
              <p className="mb-3 font-semibold">
                نتائج الحالة: {results.processed} ناجح من {results.total}
              </p>
              <ul className="space-y-2 text-sm max-h-80 overflow-y-auto">
                {results.results?.map((r, i) => (
                  <li
                    key={i}
                    className={`p-2 rounded ${r.success ? "bg-green-900/30" : "bg-red-900/30"}`}
                  >
                    <span className="font-mono" dir="ltr">
                      {r.input}
                    </span>
                    {r.orderId != null && <span> — طلب #{r.orderId}</span>}
                    {r.fromStatus && (
                      <span className="text-indigo-200">
                        {" "}
                        ({r.fromStatus} → {r.toStatus})
                      </span>
                    )}
                    {r.error && <span className="text-red-300"> — {r.error}</span>}
                    {r.success && <span className="text-green-300"> — تم</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {fincartResults && (
            <div className="mt-4 bg-black/20 rounded-xl p-4 border border-orange-500/30">
              <p className="mb-3 font-semibold">
                نتائج Fincart: {fincartResults.processed} جديد
                {fincartResults.already ? ` · ${fincartResults.already} مكرر` : ""}
                {fincartResults.skipped ? ` · ${fincartResults.skipped} متخطى` : ""}
                {" "}من {fincartResults.total}
              </p>
              <ul className="space-y-2 text-sm max-h-80 overflow-y-auto">
                {fincartResults.results?.map((r, i) => (
                  <li
                    key={i}
                    className={`p-2 rounded ${
                      !r.success
                        ? "bg-red-900/30"
                        : r.skipped
                          ? "bg-slate-800/50"
                          : r.alreadyInFincart
                            ? "bg-amber-900/30"
                            : "bg-green-900/30"
                    }`}
                  >
                    <span className="font-mono" dir="ltr">
                      {r.input}
                    </span>
                    {r.orderId != null && <span> — طلب #{r.orderId}</span>}
                    {r.fincartOrderId && (
                      <span className="text-orange-200" dir="ltr">
                        {" "}
                        · Fincart {r.fincartOrderId}
                      </span>
                    )}
                    {r.trackingNumber && (
                      <span className="text-indigo-200" dir="ltr">
                        {" "}
                        · {r.trackingNumber}
                      </span>
                    )}
                    {r.message && (
                      <span
                        className={
                          r.skipped
                            ? "text-slate-300"
                            : r.alreadyInFincart
                              ? "text-amber-200"
                              : "text-green-300"
                        }
                      >
                        {" "}
                        — {r.message}
                      </span>
                    )}
                    {r.error && <span className="text-red-300"> — {r.error}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkStatusUpdatePage;
